export type FileAsset = {
  id: string; filename: string; purpose: string; mime_type: string;
  size_bytes: number; status: string; scan_status: string; media_duration_sec?: number;
}
export type UploadState = 'identifying' | 'waiting' | 'uploading' | 'paused' | 'checking' | 'ready' | 'failed' | 'cancelled' | 'reselect'
export type UploadTask = {
  key: string; scope: string; filename: string; purpose: string; size: number;
  integrityVersion?: 'oss-md5-crc64-v1';
  fingerprint: string; fileID?: string; state: UploadState; bytes: number;
  error?: string; expiresAt?: string; asset?: FileAsset;
}
type Session = { file: FileAsset; mode: string; part_size: number; status: string; fingerprint: string; expires_at: string; parts: {part_number:number;size_bytes:number}[] }
type Grant = {file: FileAsset;mode?: string;part_size?:number;upload_url:string;headers:Record<string,string>;session_expires_at?:string}
type PartGrant = {part_number:number;upload_url:string;headers:Record<string,string>}
export type Request = <T>(path:string, init?:RequestInit) => Promise<T>
type UploadPolicy = {scope:string;purposes:Record<string,{max_size_bytes:number;mime_types:string[]}>}
type Options = {request: Request; concurrency: number; base?:string}
const delay = (ms:number) => new Promise<void>(r => setTimeout(r, ms))
const waitForCheck = (ms:number,signal?:AbortSignal) => new Promise<void>((resolve,reject) => {
 if(signal?.aborted) {reject(stopped());return}
 const finish=()=>{signal?.removeEventListener('abort',abort);resolve()}
 const timer=setTimeout(finish,ms)
 const abort=()=>{clearTimeout(timer);signal?.removeEventListener('abort',abort);reject(stopped())}
 signal?.addEventListener('abort',abort,{once:true})
})
const stopped = () => new DOMException('上传已停止', 'AbortError')
export function uploadErrorMessage(error:unknown):string {
 const key=error instanceof Error?error.message:'upload'
 return ({transfer:'文件传输失败，请重试',network:'网络连接中断，请选择原文件继续',timeout:'上传超时，请选择原文件继续',scan:'上传暂未完成，请在上传任务中重试',rejected:'文件无法使用，请重新选择文件',expired:'上传任务已过期，请重新上传',session:'登录状态已变化，请重新登录后上传','different-file':'所选文件与原文件不同，请选择原文件',signature:'暂时无法获取上传地址，请重试',upload:'上传失败，请重试'} as Record<string,string>)[key]??key
}
function mimeFor(file:File):string {
 const ext=file.name.split('.').pop()?.toLowerCase()??''
 const aliases:Record<string,string>={'audio/x-wav':'audio/wav','image/jpg':'image/jpeg'}
 const inferred:Record<string,string>={pdf:'application/pdf',doc:'application/msword',docx:'application/vnd.openxmlformats-officedocument.wordprocessingml.document',pptx:'application/vnd.openxmlformats-officedocument.presentationml.presentation',jpg:'image/jpeg',jpeg:'image/jpeg',png:'image/png',webp:'image/webp',mp3:'audio/mpeg',m4a:'audio/mp4',wav:'audio/wav',mp4:'video/mp4',webm:'video/webm'}
 return aliases[file.type]??(file.type&&file.type!=='application/octet-stream'?file.type:inferred[ext]??'application/octet-stream')
}
export async function uploadMimeFor(file:File):Promise<string> {
 const header=new Uint8Array(await file.slice(0,12).arrayBuffer())
 if(header.length>=3&&header[0]===0xff&&header[1]===0xd8&&header[2]===0xff)return 'image/jpeg'
 if(header.length>=8&&[137,80,78,71,13,10,26,10].every((v,i)=>header[i]===v))return 'image/png'
 if(header.length>=12&&String.fromCharCode(...header.slice(0,4))==='RIFF'&&String.fromCharCode(...header.slice(8,12))==='WEBP')return 'image/webp'
 return mimeFor(file)
}

// Hash every byte in bounded chunks. This is a digest of ordered chunk digests,
// not a whole-file SHA256; it protects resume identity without a large allocation.
export type UploadIntegrity = {version:'oss-md5-crc64-v1';crc64:string;part_md5:string[]}
export async function identifyUpload(file: Blob, withIntegrity=false, signal?:AbortSignal):Promise<{fingerprint:string;integrity?:UploadIntegrity}> {
  const hashes:Uint8Array[]=[]
  const algorithms=withIntegrity?await import('hash-wasm'):undefined
  const crc=algorithms?await algorithms.createCRC64():undefined
  const md5=algorithms?await algorithms.createMD5():undefined
  const partMD5:string[]=[]
  const multipart=file.size>20*1024*1024
  for(let offset=0;offset<file.size;offset+=8*1024*1024){
    if(signal?.aborted)throw stopped()
    const bytes=new Uint8Array(await file.slice(offset,offset+8*1024*1024).arrayBuffer())
    hashes.push(new Uint8Array(await crypto.subtle.digest('SHA-256',bytes)))
    crc?.update(bytes);md5?.update(bytes)
    if(md5&&multipart){partMD5.push(btoa(String.fromCharCode(...md5.digest('binary'))));md5.init()}
    // Yield between bounded chunks for keyboard, pause and status interactions.
    if(withIntegrity)await delay(0)
  }
  if(signal?.aborted)throw stopped()
  if(md5&&!multipart)partMD5.push(btoa(String.fromCharCode(...md5.digest('binary'))))
  const joined=new Uint8Array(hashes.length*32)
  hashes.forEach((hash,i)=>joined.set(hash,i*32))
  const fingerprint=Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256',joined)),n=>n.toString(16).padStart(2,'0')).join('')
  return {fingerprint,integrity:crc?{version:'oss-md5-crc64-v1',crc64:BigInt('0x'+crc.digest()).toString(10),part_md5:partMD5}:undefined}
}
export async function fingerprint(file:Blob,signal?:AbortSignal){return (await identifyUpload(file,false,signal)).fingerprint}

async function records(mode:'readwrite'|'readonly', action:(store:IDBObjectStore)=>IDBRequest) {
  if(typeof indexedDB==='undefined') return undefined
  return new Promise<unknown>((resolve,reject)=>{
    const open=indexedDB.open('welearn-uploads',1)
    open.onupgradeneeded=()=>{open.result.createObjectStore('tasks',{keyPath:'key'})}
    open.onerror=()=>reject(open.error)
    open.onsuccess=()=>{
      const db=open.result,tx=db.transaction('tasks',mode)
      const request=action(tx.objectStore('tasks'))
      tx.oncomplete=()=>{db.close();resolve(request.result)}
      tx.onerror=()=>{db.close();reject(tx.error)}
    }
  })
}

export class UploadClient {
  private tasks = new Map<string,UploadTask>()
  private invalidated = new Set<string>()
  private listeners = new Set<()=>void>()
  private controls = new Map<string,AbortController>()
  private running = new Map<string,Promise<FileAsset>>()
  private pausedWaiters = new Map<string, {resolve:(asset:FileAsset)=>void;reject:(error:unknown)=>void}[]>()
  private policyRequest?: Promise<UploadPolicy>
  private restoreRequest?:Promise<void>
  private monitoring = new Map<string,Promise<FileAsset>>()
  private generation = 0
  private active = 0
  private snapshot: UploadTask[]=[]
  private base:string
  private options:Options
  constructor(options:Options) { this.options=options; this.base=options.base??'/files' }
  subscribe=(listener:()=>void)=>{this.listeners.add(listener);return()=>{this.listeners.delete(listener)}}
  getSnapshot=()=>this.snapshot
  private publish(task:UploadTask) {
    if(this.invalidated.has(task.key))return
    this.tasks.set(task.key,task);this.snapshot=Array.from(this.tasks.values(),t=>({...t}))
    this.listeners.forEach(fn=>fn())
  }
  private async save(task:UploadTask) {
    if(this.invalidated.has(task.key))return
    this.publish(task)
    // Storage may be unavailable in privacy mode; server-side recovery still works.
    await records('readwrite',store=>store.put({...task})).catch(()=>undefined)
  }
  private async slot(signal:AbortSignal) {
    while(this.active>=this.options.concurrency) {if(signal.aborted) throw stopped();await delay(80)}
    if(signal.aborted) throw stopped()
    this.active++
    return ()=>{this.active--}
  }
  private async put(grant:PartGrant,blob:Blob,signal:AbortSignal,progress:(bytes:number)=>void) {
    const release=await this.slot(signal)
    try {await new Promise<void>((resolve,reject)=>{
      const xhr=new XMLHttpRequest()
      xhr.open('PUT',grant.upload_url);xhr.withCredentials=false;xhr.timeout=180000
      for(const [key,value] of Object.entries(grant.headers??{})) xhr.setRequestHeader(key,value)
      const abort=()=>xhr.abort()
      signal.addEventListener('abort',abort,{once:true})
      xhr.upload.onprogress=e=>progress(e.loaded)
      xhr.onload=()=>xhr.status>=200&&xhr.status<300?resolve():reject(Object.assign(new Error('transfer'),{status:xhr.status}))
      xhr.onerror=()=>reject(Object.assign(new Error('network'),{status:0}))
      xhr.ontimeout=()=>reject(Object.assign(new Error('timeout'),{status:0}))
      xhr.onabort=()=>reject(stopped())
      xhr.onloadend=()=>signal.removeEventListener('abort',abort)
      if(signal.aborted) {reject(stopped());return}
      xhr.send(blob)
    })}finally{release()}
  }
  // Share only in-flight reads. Each later batch rechecks the current identity and policy.
  private policy():Promise<UploadPolicy> {
    if(this.policyRequest)return this.policyRequest
    const pending=this.options.request<UploadPolicy>(`${this.base}/upload-policy`).finally(()=>{
      if(this.policyRequest===pending)this.policyRequest=undefined
    })
    this.policyRequest=pending
    return pending
  }
  restore():Promise<void> {
    if(this.restoreRequest)return this.restoreRequest
    const pending=this.restoreTasks().finally(()=>{if(this.restoreRequest===pending)this.restoreRequest=undefined})
    this.restoreRequest=pending
    return pending
  }
  private async restoreTasks() {
    const generation=this.generation
    const {scope}=await this.policy()
    if(generation!==this.generation)return
    const local=(await records('readonly',store=>store.getAll()).catch(()=>[])) as UploadTask[]|undefined
    if(generation!==this.generation)return
    for(const t of local??[]) {
      if(generation!==this.generation)return
      if(!this.controls.has(t.key)&&t.scope===scope && !['ready','cancelled'].includes(t.state)) this.publish({...t,state:t.fileID?'reselect':'failed'})
    }
    if(generation!==this.generation)return
    const {items}=await this.options.request<{items:Session[]}>(`${this.base}/uploads`)
    if(generation!==this.generation)return
    for(const u of items) {
      if(generation!==this.generation)return
      const previous=Array.from(this.tasks.values()).find(t=>t.fileID===u.file.id&&t.scope===scope)
      if(previous&&this.controls.has(previous.key)) continue
      const t:UploadTask={key:previous?.key??crypto.randomUUID(),scope,filename:u.file.filename,purpose:u.file.purpose,size:u.file.size_bytes,fingerprint:u.fingerprint,fileID:u.file.id,bytes:u.status==='complete'?u.file.size_bytes:u.parts.reduce((sum,part)=>sum+part.size_bytes,0),state:u.status==='complete'?'checking':'reselect',expiresAt:u.expires_at,asset:u.file}
      if(u.file.scan_status==='failed') {t.state='failed';t.error='scan'}
      await this.save(t)
      if(generation!==this.generation)return
      if(t.state==='checking') void this.monitor(t).catch(()=>undefined)
    }
  }
  async upload(file:File,purpose:string,resumeKey?:string):Promise<FileAsset> {
    const generation=this.generation
    const policy=await this.policy()
    const {scope}=policy
    if(generation!==this.generation)throw stopped()
    const rule=policy.purposes[purpose]
    if(!rule)throw new Error('不支持此用途的文件上传')
    if(file.size<=0||file.size>rule.max_size_bytes)throw new Error(`请选择非空且不超过 ${Math.round(rule.max_size_bytes/1024/1024)} MiB 的文件`)
    const declaredMime=await uploadMimeFor(file)
    if(!rule.mime_types.includes(declaredMime))throw new Error('该用途不支持此文件类型，请重新选择')
    const t=resumeKey?this.tasks.get(resumeKey):undefined
    if(t&&t.scope!==scope) throw new Error('session')
    if(t&&this.running.has(t.key)) return this.running.get(t.key)!
    const task:UploadTask=t??{key:crypto.randomUUID(),scope,filename:file.name,purpose,size:file.size,integrityVersion:purpose==='lesson_media'?'oss-md5-crc64-v1':undefined,fingerprint:'',state:'identifying',bytes:0}
    const promise=this.transfer(task,file,declaredMime)
    this.running.set(task.key,promise)
    try {
      let asset:FileAsset
      try {asset=await promise} finally {this.running.delete(task.key)}
      for(const waiter of this.pausedWaiters.get(task.key)??[])waiter.resolve(asset)
      this.pausedWaiters.delete(task.key)
      return asset
    } catch(error) {
      if(task.state==='paused')return new Promise<FileAsset>((resolve,reject)=>{const waiters=this.pausedWaiters.get(task.key)??[];waiters.push({resolve,reject});this.pausedWaiters.set(task.key,waiters)})
      for(const waiter of this.pausedWaiters.get(task.key)??[])waiter.reject(error)
      this.pausedWaiters.delete(task.key)
      throw error
    }
  }
  private async transfer(t:UploadTask,file:File,mime:string):Promise<FileAsset> {
    const control=new AbortController();this.controls.set(t.key,control)
    try {
      t.state='identifying';t.error=undefined;this.publish(t)
      const identity=await identifyUpload(file,t.integrityVersion==='oss-md5-crc64-v1'&&!t.fileID,control.signal)
      const hash=identity.fingerprint
      if(t.fingerprint&&(t.fingerprint!==hash||file.size!==t.size)) throw new Error('different-file')
      if(!t.fileID) {
        const same=Array.from(this.tasks.values()).find(v=>v.key!==t.key&&v.scope===t.scope&&v.purpose===t.purpose&&v.size===file.size&&v.fingerprint===hash&&v.fileID&&v.asset?.status!=='quarantined'&&!['cancelled'].includes(v.state))
        if(same) {t.fileID=same.fileID;t.expiresAt=same.expiresAt}
      }
      t.fingerprint=hash;t.state='waiting';await this.save(t)
      if(control.signal.aborted) throw stopped()
      let grant:Grant|undefined
      const resuming=Boolean(t.fileID)
      if(!t.fileID) {
        grant=await this.options.request<Grant>(this.base,{method:'POST',body:JSON.stringify({request_key:t.key,fingerprint:hash,integrity:identity.integrity,filename:file.name,purpose:t.purpose,mime_type:mime,size_bytes:file.size})})
        t.fileID=grant.file.id;t.expiresAt=grant.session_expires_at;t.asset=grant.file;await this.save(t)
        if(control.signal.aborted) { if(this.tasks.get(t.key)?.state==='cancelled') await this.options.request(`${this.base}/${t.fileID}/upload`,{method:'DELETE'});throw stopped() }
      }
      const session=await this.options.request<Session>(`${this.base}/${t.fileID}/upload`)
      if(session.status==='complete') {
        if(control.signal.aborted)throw stopped()
        // A new user attempt may resume a completed transfer whose check failed.
        // Retry only that recoverable state, never quarantine or a pending scan.
        if(resuming&&session.file.status==='uploaded'&&session.file.scan_status==='failed') {
          await this.options.request(`${this.base}/${t.fileID}/scan/retry`,{method:'POST',signal:control.signal})
        }
        return await this.check(t,control.signal)
      }
      if(session.status==='assembled'||session.status==='confirming') {await this.confirm(t,control.signal);return await this.check(t,control.signal)}
      if(session.status==='cancelled'||new Date(session.expires_at).getTime()<=Date.now()) throw new Error('expired')
      const done=new Map(session.parts.map(p=>[p.part_number,p.size_bytes]))
      const progress=new Map<number,number>()
      const partSize=session.mode==='multipart'?session.part_size:file.size
      const count=Math.ceil(file.size/partSize)
      let next=1
      t.state='uploading'
      const part=async()=>{
        while(next<=count) {
          const n=next++
          if(done.has(n)) continue
          const body=file.slice((n-1)*partSize,Math.min(n*partSize,file.size))
          for(let attempt=0;;attempt++) {
            if(control.signal.aborted) throw stopped()
            try {
              const signed=await this.options.request<{parts:PartGrant[]}>(`${this.base}/${t.fileID}/upload/sign`,{method:'POST',body:JSON.stringify({part_numbers:session.mode==='multipart'?[n]:[]})})
              const grant=signed.parts[0]
              if(!grant) throw new Error('signature')
              await this.put(grant,body,control.signal,bytes=>{
                progress.set(n,Math.min(bytes,body.size));t.bytes=Array.from(done.values()).reduce((a,b)=>a+b,0)+Array.from(progress.values()).reduce((a,b)=>a+b,0);this.publish(t)
              })
              progress.delete(n);done.set(n,body.size);break
            }catch(error) {
              const status=(error as {status?:number}).status
              if(control.signal.aborted||attempt>=3||(status!==undefined&&status!==0&&status!==403&&status!==429&&status<500)) throw error
              await delay(500*2**attempt)
            }
          }
        }
      }
      // All sibling part requests stop on failure; a later retry asks OSS for truth.
      await Promise.all(Array.from({length:Math.min(count,this.options.concurrency)},part)).catch(error=>{control.abort();throw error})
      if(control.signal.aborted) throw stopped()
      await this.confirm(t,control.signal)
      t.bytes=file.size
      return await this.check(t,control.signal)
    }catch(error) {
      if(t.state!=='cancelled'&&t.state!=='paused') {t.state='failed';t.error=error instanceof Error?error.message:'upload'}
      await this.save(t);throw error
    }finally{this.controls.delete(t.key)}
  }
  private async confirm(t:UploadTask,signal:AbortSignal) {
    for(let attempt=0;;attempt++) {
      if(signal.aborted)throw stopped()
      try {await this.options.request(`${this.base}/${t.fileID}/confirm`,{method:'POST',signal});return} catch(error) {
        const status=(error as {status?:number}).status
        if(signal.aborted||attempt>=5||(status!==409&&status!==0&&status!==undefined&&status<500))throw error
        const session=await this.options.request<Session>(`${this.base}/${t.fileID}/upload`,{signal})
        if(session.status==='complete')return
        await delay(Math.min(1000*2**attempt,10000))
      }
    }
  }
  private monitor(t:UploadTask):Promise<FileAsset> {
    const previous=this.monitoring.get(t.key);if(previous)return previous
    const c=new AbortController();this.controls.set(t.key,c)
    const pending=this.check(t,c.signal).catch(async error=>{if(!c.signal.aborted){t.state='failed';t.error=error instanceof Error?error.message:'network';await this.save(t)}throw error}).finally(()=>{if(this.controls.get(t.key)===c)this.controls.delete(t.key);if(this.monitoring.get(t.key)===pending)this.monitoring.delete(t.key)})
    this.monitoring.set(t.key,pending)
    return pending
  }
  private async check(t:UploadTask,signal?:AbortSignal):Promise<FileAsset> {
    t.state='checking';t.error=undefined;await this.save(t)
    const started=Date.now();let polls=0
    while(true) {
      if(signal?.aborted) throw stopped()
      const a=await this.options.request<FileAsset>(`${this.base}/${t.fileID}`,{signal})
      if(signal?.aborted) throw stopped()
      t.asset=a
      if(isAssetReady(a)) {t.state='ready';t.bytes=t.size;await this.save(t);return a}
      if(a.status==='quarantined'||['failed','infected'].includes(a.scan_status)) {t.state='failed';t.error=a.status==='quarantined'||a.scan_status==='infected'?'rejected':'scan';await this.save(t);throw new Error(t.error)}
      polls++
      // A slow scanner remains checking; never return a pending asset as ready.
      // Bound the fast polling phase and continue at a low recovery cadence.
      const hidden=typeof document!=="undefined"&&document.visibilityState==="hidden"
      const interval=hidden||Date.now()-started>=300_000?60_000:polls>=30?30_000:polls>=10?10_000:2000
      await waitForCheck(interval,signal)
    }
  }
  pause(key:string) {const t=this.tasks.get(key);if(!t||!['uploading','waiting','identifying'].includes(t.state))return;t.state='paused';this.controls.get(key)?.abort();void this.save(t)}
  async cancel(key:string) {
    const t=this.tasks.get(key);if(!t||['ready','checking'].includes(t.state))return
    this.controls.get(key)?.abort()
    try {
      if(t.fileID)await this.options.request(`${this.base}/${t.fileID}/upload`,{method:'DELETE'})
      t.state='cancelled';t.error=undefined;await this.save(t)
      for(const waiter of this.pausedWaiters.get(key)??[])waiter.reject(stopped())
      this.pausedWaiters.delete(key)
    } catch(error) {t.state='failed';t.error='cancel-failed';await this.save(t);throw error}
  }
  async retryScan(key:string) {const t=this.tasks.get(key);if(!t?.fileID)return;await this.options.request(`${this.base}/${t.fileID}/scan/retry`,{method:'POST'});return this.monitor(t)}
  async dismiss(key:string) {const t=this.tasks.get(key);if(!t||!['ready','cancelled'].includes(t.state)&&t.error!=='rejected')return;this.tasks.delete(key);this.snapshot=Array.from(this.tasks.values());this.listeners.forEach(fn=>fn());await records('readwrite',s=>s.delete(key)).catch(()=>undefined)}
  async clearSession() {
    this.generation++
    this.policyRequest=undefined
    this.restoreRequest=undefined
    this.monitoring.clear()
    for(const waiters of this.pausedWaiters.values())for(const waiter of waiters)waiter.reject(stopped())
    this.pausedWaiters.clear()
    for(const key of this.tasks.keys()) this.invalidated.add(key)
    for(const c of this.controls.values()) c.abort()
    for(const t of this.tasks.values()) await records('readwrite',s=>s.delete(t.key)).catch(()=>undefined)
    this.tasks.clear();this.snapshot=[];this.listeners.forEach(fn=>fn())
  }
}

/** ready means server-side integrity/content validation has completed. */
export function isAssetReady(a: Pick<FileAsset, 'status' | 'scan_status' | 'purpose' | 'mime_type'>): boolean {
  return a.status === 'ready' && (a.scan_status === 'clean' || (
    a.scan_status === 'not_required' && a.purpose === 'lesson_media' &&
    ['audio/mpeg', 'audio/mp4', 'audio/wav', 'video/mp4', 'video/webm'].includes(a.mime_type)
  ))
}
